import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import {
  useGetHostsQuery,
  useSetHostActiveMutation,
  useRemoveHostMutation,
} from '../../api/peopleApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import StatusDots from '../../components/StatusDots/StatusDots';
import s from '../../styles/shared/page.module.css';
import buttons from '../../styles/shared/buttons.module.css';
import table from '../../styles/shared/table.module.css';

export default function HostsPage() {
  const navigate = useNavigate();
  const showToast = useToast();

  const {
    data: hosts,
    isLoading,
    isError,
    error,
  } = useGetHostsQuery();
  const [setActive] = useSetHostActiveMutation();
  const [removeHost] = useRemoveHostMutation();

  useEffect(() => {
    if (isError) showToast('error', 'Hosts', errMsg(error, 'Could not load hosts.'));
  }, [isError, error, showToast]);

  const tooltipRef = useRef(null);
  useEffect(() => {
    tooltipRef.current?.updateTargetEvents();
  }, [hosts]);

  async function handleToggleActive(host) {
    try {
      await setActive({ id: host.id, isActive: !host.is_active }).unwrap();
      showToast('success', 'Hosts', host.is_active ? 'Host disabled.' : 'Host enabled.');
    } catch (err) {
      showToast('error', 'Hosts', errMsg(err, 'Could not update host.'));
    }
  }

  async function handleRemove(host) {
    try {
      await removeHost(host.id).unwrap();
      showToast('success', 'Hosts', 'Host removed.');
    } catch (err) {
      showToast('error', 'Hosts', errMsg(err, 'Could not remove host.'));
    }
  }

  const goEdit = (host) => navigate(`${host.id}/edit`);

  const actionBodyTemplate = (host) => (
    <div className={table.actions}>
      <Button icon="pi pi-pencil" text className={table.iconNeutral} onClick={() => goEdit(host)} />
      <Button
        icon={`pi ${host.is_active ? 'pi-ban' : 'pi-check'}`}
        text
        className={host.is_active ? table.iconDisable : table.iconSuccess}
        onClick={() => handleToggleActive(host)}
      />
      <Button
        icon="pi pi-trash"
        text
        className={table.iconDanger}
        onClick={() => handleRemove(host)}
      />
    </div>
  );

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <h1>Hosts</h1>
          <p className={`${s.subtitle} textMuted`}>Giveaway hosts for your organization.</p>
        </div>
        <Button
          label="Add host"
          icon="pi pi-plus"
          className={buttons.btnPrimary}
          onClick={() => navigate('create')}
        />
      </div>

      <div className={`${table.tableWrapper} ${table.reverseCard}`}>
        <Tooltip ref={tooltipRef} target={`.${table.statusDot}`} position="left" />
        <DataTable
          value={hosts ?? []}
          loading={isLoading}
          emptyMessage="No hosts yet. Add one above."
          responsiveLayout="stack"
        >
          <Column
            field="name"
            header="Name"
            className={table.contactCol}
            body={(h) => <div className={`${table.cellInsider} ${table.cellWrap}`}>{h.name}</div>}
          />
          <Column
            header="Email"
            className={`${s.mutedCell} ${table.contactCol}`}
            body={(h) => <div className={table.cellInsider}>{h.email}</div>}
          />
          <Column
            header="Status"
            className={table.statusCol}
            body={(h) => (
              <div className={table.cellInsider}>
                <div className={`${table.statusBadgesDesktop} ${h.is_active ? s.statusOk : s.statusDisabled}`}>
                  {h.is_active ? 'active' : 'disabled'}
                </div>
                <StatusDots
                  items={[
                    {
                      label: h.is_active ? 'active' : 'disabled',
                      color: h.is_active ? 'var(--color-success)' : 'var(--color-error)',
                    },
                  ]}
                />
              </div>
            )}
          />
          <Column
            header="Actions"
            className={table.actionsCol}
            body={actionBodyTemplate}
            style={{ width: '250px' }}
          />
        </DataTable>
      </div>
    </div>
  );
}
